import { useRecoilValue } from "recoil"
import React, { useState } from 'react'
import { currentPatientFileInfoAtom } from "../../../../../Pages/Data/Visualize/DataReview/Atoms/PatientFile"
import { MobergButton, MobergButtonShape, MobergButtonVariant, MobergColumn, MobergRow, MobergTheme } from "../../../../../Moberg"
import { useModalProvider } from "../../../../../Providers/ModalProvider"
import { HarmonizedMedicationSelector } from "./HarmonizedMedicationSelector"
import { useAsyncTask } from "../../../../../Hooks/useAsyncTask"
import { useEndpointProvider } from "../../../../../Providers/EndpointProvider"
import ConfirmationModal from "../../../../../Components/ConfirmationModal/ConfirmationModal"
import { useBackendLinksProvider } from "../../../../../Providers/BackendLinksProvider"
import { MedicationInfo } from "./Medications"
import { useEMRDataProvider } from "../../../../../Providers/EMRDataProvider"

export const MedicationHarmonizer = () => {
    const { post } = useEndpointProvider()
    const { createModal } = useModalProvider()
    const { updatePatientMedications } = useEMRDataProvider()
    const { patientIdentifier, patientMedications } = useRecoilValue(currentPatientFileInfoAtom)
    const [assignments, setAssignments] = useState<Record<string, Record<string, any>>>({}) // Medication code to harmonized medication ID
    const { LINKS } = useBackendLinksProvider() 
    const updateMedicationMappingTask = useAsyncTask(() => {
        const medicationInfoRecords = Object.entries(assignments)
            .map(([code, record]) => ({
                harmonizedId: record["harmonizedId"],
                name: record["name"],
                code
            })
        )

        return post(LINKS.PATIENT.UPDATE_HARMONIZED_MEDICATION_MAPPING, { medicationInfoRecords })
            .then(() => updatePatientMedications(patientIdentifier))
            .then(() => setAssignments({}))
            .catch(error => {
                createModal(<ConfirmationModal 
                    title={"Couldn't update medications"}
                    description={`${error}`}
                    confirmButton={{ text: "OK", theme: MobergTheme.BLUE }}
                    showCancelButton={false}
                />)
            })
    })

    const handleAssignment = (medication: MedicationInfo, harmonizedId: number) => {
        setAssignments(prev => ({
            ...prev,
            [medication.code]: {harmonizedId, name: medication.name}
        }))
    }

    const submitDisabledReason = (Object.keys(assignments).length === 0 ? "Must assign at least one medication" : "")
        || (updateMedicationMappingTask.isWaiting ? "Saving medication mapping..." : "")


    return (
        <MobergColumn gap="16px" style={{ padding: "16px" }}>
            <MobergColumn gap="16px">
                {patientMedications.unharmonized.map(medication => (
                    <MobergColumn key={medication.code}>
                        <MobergRow horizontalAlign="space-between" gap="16px">
                            <div>
                                <strong>{medication.name}</strong>
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    Code: {medication.code}
                                </div>
                            </div>

                            <HarmonizedMedicationSelector
                                highlight={false}
                                onChange={newMedication => handleAssignment(medication, Number(newMedication.id))}
                            />
                        </MobergRow>
                    </MobergColumn>
                ))}
            </MobergColumn>

            <MobergRow horizontalAlign="right">
                <MobergButton
                    theme={MobergTheme.BLUE}
                    variant={MobergButtonVariant.FILLED}
                    shape={MobergButtonShape.WIDE}
                    onClick={updateMedicationMappingTask.run}
                    disabled={!!submitDisabledReason}
                    tooltip={submitDisabledReason}
                >
                    Assign
                </MobergButton>
            </MobergRow>
        </MobergColumn>
    )
}
